import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import cn from 'classnames';

import MaskedInput from 'react-text-mask';
import './style.scss';

const TOOLTIP_WIDTH = 280;

const Input = ({
  isRequired = false,
  className = '',
  labelClassName = '',
  value = '',
  isDisabled = false,
  label = null,
  hasError = false,
  errorMessage = '',
  isBadgeVisible = true,
  placeHolder = '',
  isMasked = false,
  notification = null,
  readOnly = false,
  isErrorVisible = true,
  ...restProps
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isRequiredInternal, setIsRequiredInternal] = useState(false);
  const [isNotificationShowing, setIsNotificationShowing] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const autocomplete = useRef(null);
  const tooltipTriggerRef = useRef(null);
  const tooltipContentRef = useRef(null);
  const { onFocus: onFocusProp, onBlur: onBlurProp, ...inputProps } = restProps;

  const handleFocus = (e) => {
    setIsFocused(true);
    if (onFocusProp) onFocusProp(e);
  };

  const handleBlur = (e) => {
    setIsRequiredInternal(e.target.value ? false : isRequired);
    if (!isRequired) setIsFocused(false);
    if (onBlurProp) onBlurProp(e);
  };

  const updateTooltipPosition = () => {
    if (!tooltipTriggerRef.current) return;
    const rect = tooltipTriggerRef.current.getBoundingClientRect();
    setTooltipPosition({
      top: rect.bottom + 8,
      left: Math.min(Math.max(12, rect.right - TOOLTIP_WIDTH), window.innerWidth - TOOLTIP_WIDTH - 12),
    });
  };

  const showNotification = () => {
    updateTooltipPosition();
    setIsNotificationShowing(true);
  };

  const hideNotification = () => setIsNotificationShowing(false);

  const toggleNotification = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isNotificationShowing) hideNotification();
    else showNotification();
  };

  const handleNotificationKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') toggleNotification(e);
    if (e.key === 'Escape') hideNotification();
  };

  useLayoutEffect(() => {
    if (!isNotificationShowing) return undefined;
    updateTooltipPosition();
    const reposition = () => updateTooltipPosition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [isNotificationShowing]);

  useEffect(() => {
    if (!isNotificationShowing) return undefined;
    const onPointerDown = (event) => {
      const inTrigger = tooltipTriggerRef.current?.contains(event.target);
      const inContent = tooltipContentRef.current?.contains(event.target);
      if (!inTrigger && !inContent) hideNotification();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isNotificationShowing]);

  const sharedInputProps = {
    className: cn(className, 'input'),
    value,
    disabled: isDisabled,
    onFocus: handleFocus,
    onBlur: handleBlur,
    ref: autocomplete,
    readOnly,
    ...inputProps,
  };

  return (
    <label
      className={cn(
        {
          inputLabel: true,
          'has-value': isFocused || value.length || isDisabled || hasError,
          required: isRequiredInternal || hasError,
          disabled: isDisabled,
          'has-notification': !!notification,
        },
        labelClassName
      )}
    >
      {label && (
        <span>
          {label}
          {isBadgeVisible && !isDisabled && (
            <em>
              {isRequired && errorMessage === 'The input field is required' && 'Required'}
              {isRequired && errorMessage !== 'The input field is required' && hasError && 'Error'}
              {!isRequired && errorMessage !== 'The input field is required' && hasError && 'Error'}
              {isRequired && !hasError && 'Required'}
              {!isRequired && !hasError && 'Optional'}
            </em>
          )}
        </span>
      )}
      {isMasked ? (
        <MaskedInput
          {...sharedInputProps}
          mask={isMasked}
          placeholder={isFocused ? placeHolder : ''}
        />
      ) : (
        <input
          {...sharedInputProps}
          placeholder={(isFocused || value.length || isDisabled || hasError) ? placeHolder : ''}
        />
      )}
      {notification && (
        <div className="input-info-tooltip">
          <i
            ref={tooltipTriggerRef}
            role="button"
            tabIndex={0}
            aria-label="More information"
            aria-expanded={isNotificationShowing}
            onMouseEnter={showNotification}
            onMouseLeave={hideNotification}
            onClick={toggleNotification}
            onKeyDown={handleNotificationKeyDown}
          >
            i
          </i>
          {isNotificationShowing
            && createPortal(
              <div
                ref={tooltipContentRef}
                className="input-info-tooltip-portal"
                role="tooltip"
                style={tooltipPosition}
                onMouseEnter={showNotification}
                onMouseLeave={hideNotification}
              >
                {notification}
              </div>,
              document.body
            )}
        </div>
      )}
      {isErrorVisible && !readOnly && errorMessage && errorMessage !== 'The input field is required' && (
        <div className="error">{errorMessage}</div>
      )}
    </label>
  );
};

Input.propTypes = {
  isRequired: PropTypes.bool,
  className: PropTypes.string,
  labelClassName: PropTypes.string,
  value: PropTypes.string,
  max: PropTypes.number,
  isDisabled: PropTypes.bool,
  label: PropTypes.node,
  hasError: PropTypes.bool,
  hasValue: PropTypes.bool,
  errorMessage: PropTypes.string,
  type: PropTypes.string,
  onChange: PropTypes.func,
  isBadgeVisible: PropTypes.bool,
  placeHolder: PropTypes.string,
  isMasked: PropTypes.any,
  notification: PropTypes.node,
  readOnly: PropTypes.bool,
  isErrorVisible: PropTypes.bool,
};

Input.defaultProps = {
  isRequired: false,
  isDisabled: false,
  notification: null,
  isErrorVisible: true,
  onChange: () => {},
  errorMessage: '',
  isBadgeVisible: true,
  isMasked: false,
  hasValue: false,
  hasError: false,
  readOnly: false,
  type: 'text',
  placeHolder: '',
  max: 0,
  label: null,
  value: '',
  className: '',
  labelClassName: '',
};

export default Input;
